// FrozenLabelerModal.js
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CalculateIcon from "@mui/icons-material/Calculate";
import ArticleIcon from "@mui/icons-material/Article";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import Div from "@jumbo/shared/Div";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import { numberFormat } from "../../../utils/numberFunctions";
import SelectInventoryForm from "./SelectInventoryForm";
import { saveLabel } from "../../../utils/fulfilment/labels";

// ---------- Helpers ----------
const addYears = (d, n) => {
  const x = new Date(d);
  x.setFullYear(x.getFullYear() + n);
  return x;
};
const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
const BEST_BEFORE_YMD = ymd(addYears(new Date(), 1));

const withBestBefore = (stockItem) => ({
  ...stockItem,
  BestBefore: BEST_BEFORE_YMD,
  bestBefore: BEST_BEFORE_YMD,
  bbDate: BEST_BEFORE_YMD,
});

// Trigger labels.js frozen rules (Best Before = print date + 1 year)
const EXTRA_CONTEXT = { context: "frozen_labeler", printDate: new Date().toISOString() };

// ---------- Main Modal ----------
export default function FrozenLabelerModal({ open, onClose, warehouseId, access }) {
  const [outputs, setOutputs] = React.useState([]);

  const selectedList = React.useMemo(
    () =>
      outputs.map((o) => ({
        lotSerialNbr: o.ivn.lotSerialNbr,
        stockItemId: o.ivn.stockItemId ?? o.ivn?.stockItem?.id,
        expiryDate: o.ivn.expiryDate,
      })),
    [outputs]
  );

  const onPick =
    (invRows) =>
    (_, { dataIndex }) => {
      const row = invRows[dataIndex];
      const frozenFlag =
        row.isFrozen ||
        /frozen/i.test(row?.stockItem?.description || "") ||
        /frozen/i.test(row?.category || "");
      if (!frozenFlag) return;

      const exists = outputs.find(
        (o) =>
          o.ivn.lotSerialNbr === row.lotSerialNbr &&
          (o.ivn.stockItemId ?? o.ivn?.stockItem?.id) ===
            (row.stockItemId ?? row?.stockItem?.id) &&
          o.ivn.expiryDate === row.expiryDate
      );
      if (!exists) {
        setOutputs((prev) => [
          ...prev,
          {
            id: `${row.lotSerialNbr}_${row.stockItemId}_${row.expiryDate}`,
            ivn: row,
            quantity: 0,
          },
        ]);
      }
    };

  const totalAllocated = React.useMemo(
    () => outputs.reduce((sum, o) => sum + (Number(o.quantity) || 0), 0),
    [outputs]
  );

  const closeAll = () => {
    setOutputs([]);
    onClose && onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={closeAll}
      fullWidth
      maxWidth={false}
      PaperProps={{ style: { width: "95%", maxWidth: 1800 } }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        Frozen Regrade — Create Labels
        <IconButton
          aria-label="close"
          onClick={closeAll}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.600" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* shrink spacing and give more width to the right panel */}
      <Grid container spacing={1} p={2} sx={{ alignItems: "stretch" }}>
        {/* LEFT: Inventory picker (Frozen-only) */}
        <Grid item xs={12} md={6}>
          <SelectInventoryForm
            warehouseId={warehouseId}
            hideLive
            hideHalfshell
            hideFrozenButton
            typeDefault={3} // Frozen
            onClick={onPick}
            selectedList={selectedList}
            addFulfilment={false}
            reportTable={false}
            shrinkTable
          />
        </Grid>

        {/* RIGHT: PRN List */}
        <Grid item xs={12} md={6}>
          <PRNList outputs={outputs} setOutputs={setOutputs} />
          <Grid item xs={12} mt={1}>
            <Typography variant="h3" mb={0}>
              Total Allocated: {totalAllocated ? numberFormat(totalAllocated) : 0}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Dialog>
  );
}

// ---------- PRN List ----------
const PRNList = ({ outputs, setOutputs }) => {
  const Swal = useSwalWrapper();
  const [editUnitsStore, setEditUnitsStore] = React.useState(outputs.map(() => null));
  const [openCB, setOpenCB] = React.useState(false);
  const [comboLabelUnits, setComboLabelUnits] = React.useState(null);
  const [comboLabelAllocations, setComboLabelAllocations] = React.useState([]);
  const [calc, setCalc] = React.useState(false);
  const [calcAdd, setCalcAdd] = React.useState(0);
  const [calcVals, setCalcVals] = React.useState({ ind: null, val: null });

  React.useEffect(() => {
    setEditUnitsStore((prev) => {
      const cp = [...prev];
      if (cp.length < outputs.length) return cp.concat(Array(outputs.length - cp.length).fill(null));
      else if (cp.length > outputs.length) return cp.slice(0, outputs.length);
      return cp;
    });
  }, [outputs.length]);

  const updateLine = (index, field) => (val) => {
    const cp = [...outputs];
    cp[index][field] = field === "quantity" ? Number(val) : val;
    setOutputs(cp);
  };

  const removeLine = (ind) => () => {
    const cp = [...outputs];
    cp.splice(ind, 1);
    setOutputs(cp);
  };

  const updateEditUnitsStore = (ind) => (val) => {
    const cp = [...editUnitsStore];
    cp[ind] = val;
    setEditUnitsStore(cp);
  };

  const openCalc = (ind, val) => {
    setCalc(true);
    setCalcAdd(0);
    setCalcVals({ ind, val });
  };
  const addToTotal = () => {
    setCalc(false);
    if (calcVals.ind != null) {
      updateLine(calcVals.ind, "quantity")(Number(calcVals.val) + Number(calcAdd));
    }
  };

  // When Combo modal is open, clicking a PRN adds it with BLANK qty ("")
  const addtoCombo = (pn) => () => {
    const list = [...comboLabelAllocations];
    const exists = list.find(
      (o) =>
        o.pn.ivn.stockItemId === pn.ivn.stockItemId &&
        o.pn.ivn.lotSerialNbr === pn.ivn.lotSerialNbr &&
        o.pn.ivn.expiryDate === pn.ivn.expiryDate
    );
    if (!exists) list.push({ pn, quantity: "" });
    setComboLabelAllocations(list);
  };

  const openComboBuilder = () => setOpenCB(true);
  const closeComboBuilder = () => {
    setOpenCB(false);
    setComboLabelAllocations([]);
  };

  // SINGLE LABEL
  const singlePrint = (pn, ind) => () => {
    const codeInfo = withBestBefore(pn?.ivn?.stockItem || {});
    const editUnits = editUnitsStore[ind];
    saveLabel([pn], { customer: {} }, codeInfo, false, editUnits, null, EXTRA_CONTEXT);
  };

  // COMBO LABEL — normalize quantities (blank -> 0)
  const comboPrint = () => {
    if (comboLabelAllocations.length === 0) return;

    const normalized = comboLabelAllocations.map((a) => ({
      pn: a.pn,
      quantity: a.quantity === "" ? 0 : Number(a.quantity) || 0,
    }));

    const first = normalized[0].pn;
    const codeInfo = withBestBefore(first?.ivn?.stockItem || {});
    const labelUnits =
      comboLabelUnits == null || comboLabelUnits === "" ? null : Number(comboLabelUnits);

    const sum = normalized.reduce((a, b) => a + b.quantity, 0);

    const doPrint = () => {
      saveLabel([first], { customer: {} }, codeInfo, false, labelUnits, normalized, EXTRA_CONTEXT);
      setComboLabelAllocations([]);
      setOpenCB(false);
    };

    if (labelUnits && sum !== Number(labelUnits)) {
      Swal.fire({
        html: "Total of each PRN should equal total on label. Are you sure you want to continue?",
        icon: "warning",
        title: "Oops...",
        showCancelButton: true,
        confirmButtonText: "Yes, Continue",
        cancelButtonText: "No, I'll edit it",
      }).then((res) => {
        if (res.value) doPrint();
      });
    } else {
      doPrint();
    }
  };

  return (
    <Div>
      {outputs.map((pn, ind) => {
        const portalTotal = pn.ivn?.portalQuantity ?? "";
        const useBy = pn?.ivn?.expiryDate ? new Date(pn.ivn.expiryDate).toLocaleDateString() : "";
        const description = `${pn.ivn.stockItem?.description || ""}${
          portalTotal !== "" ? ` (${portalTotal})` : ""
        }`;

        return (
          <Grid
            container
            spacing={1}
            alignItems="center"
            mb={1}
            key={pn.id}
            sx={{ flexWrap: "nowrap" }} // don't wrap this row
            onClick={openCB ? addtoCombo(pn) : undefined}
          >
            {/* Text column — more space; prevent wrapping */}
            <Grid item xs={8} md={9} lg={10} xl={10}>
              <Typography variant="h5" mb={0}>
                {pn.ivn.lotSerialNbr}
                {useBy ? ` (Use by: ${useBy})` : ""}
              </Typography>
              <Typography
                variant="h5"
                mb={0}
                noWrap
                title={description}
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {description}
              </Typography>
            </Grid>

            {/* Quantity — narrower so it doesn't push text to wrap */}
            <Grid item xs="auto">
              <TextField
                label="Quantity"
                value={pn.quantity ?? 0}
                onChange={(e) => updateLine(ind, "quantity")(e.target.value)}
                type="number"
                size="small"
                inputProps={{ min: 0 }}
                sx={{ width: 96 }} // was 110
              />
            </Grid>

            {/* Icons */}
            <Grid item xs="auto">
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <IconButton aria-label="calc" onClick={() => openCalc(ind, pn.quantity)}>
                  <CalculateIcon />
                </IconButton>
                <IconButton aria-label="label" onClick={singlePrint(pn, ind)}>
                  <ArticleIcon />
                </IconButton>
                {editUnitsStore[ind] != null ? (
                  <TextField
                    label="Label Oys/Unit"
                    type="number"
                    size="small"
                    value={editUnitsStore[ind]}
                    onChange={(e) => updateEditUnitsStore(ind)(e.target.value)}
                    sx={{ width: 100 }}
                  />
                ) : (
                  <IconButton aria-label="edit" onClick={() => updateEditUnitsStore(ind)(null)}>
                    <BorderColorIcon />
                  </IconButton>
                )}
                <IconButton aria-label="remove" onClick={removeLine(ind)}>
                  <CloseIcon />
                </IconButton>
              </div>
            </Grid>
          </Grid>
        );
      })}

      {/* Footer: Combo button */}
      <Grid item xs={12}>
        <Button variant="contained" color="primary" onClick={() => setOpenCB(true)}>
          Combo Label
        </Button>
      </Grid>

      {/* Combo popup */}
      {openCB ? (
        <Div
          style={{
            position: "absolute",
            top: "40%",
            left: "40%",
            transform: "translate(-50%, -50%)",
            background: "white",
            border: "2px solid #000",
            boxShadow: 24,
            maxHeight: "90%",
            padding: 20,
            overflow: "auto",
            zIndex: 1000,
          }}
        >
          <IconButton
            aria-label="close"
            onClick={closeComboBuilder}
            style={{ position: "absolute", right: "1%", top: "1%" }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h5">Choose Allocations to Combine</Typography>
          <Typography sx={{ mt: 2 }}>
            {comboLabelAllocations.map((o, i) => (
              <Grid container spacing={2} mb={1} key={o.pn.id}>
                <Typography sx={{ padding: "10px", marginTop: "10px" }}>
                  {o.pn.ivn.lotSerialNbr}
                </Typography>
                <TextField
                  label="doz"
                  type="number"
                  size="small"
                  value={comboLabelAllocations[i].quantity ?? ""} // blank until typed
                  inputProps={{ min: 0 }}
                  onChange={(e) => {
                    const cp = [...comboLabelAllocations];
                    const raw = e.target.value;
                    cp[i].quantity = raw === "" ? "" : Math.max(0, Number(raw) || 0);
                    setComboLabelAllocations(cp);
                  }}
                  sx={{ width: 70 }}
                />
              </Grid>
            ))}
          </Typography>
          <TextField
            label="Label Oys/Unit"
            type="number"
            size="small"
            value={comboLabelUnits ?? ""}
            onChange={(e) => setComboLabelUnits(e.target.value)}
            sx={{ width: 120 }}
          />
          <Button sx={{ mt: 1, ml: 1 }} variant="contained" color="primary" onClick={comboPrint}>
            Print Label
          </Button>
        </Div>
      ) : null}

      {/* Mini calc */}
      {calc ? (
        <Div
          style={{
            position: "absolute",
            top: "40%",
            left: "70%",
            transform: "translate(-50%, -50%)",
            background: "white",
            border: "2px solid #000",
            boxShadow: 24,
            maxHeight: "90%",
            padding: 20,
            overflow: "auto",
            zIndex: 1000,
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => setCalc(false)}
            style={{ position: "absolute", right: "1%", top: "1%" }}
          >
            <CloseIcon />
          </IconButton>
          <Typography variant="h5">Add to Total</Typography>
          <TextField
            type="number"
            size="small"
            value={calcAdd}
            onChange={(e) => setCalcAdd(e.target.value)}
            sx={{ width: 100 }}
          />
          <Button sx={{ mt: 1 }} variant="contained" color="primary" onClick={addToTotal}>
            Add
          </Button>
        </Div>
      ) : null}
    </Div>
  );
};
